import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Simulation } from '@/data/mockAgents';
import {
  Play,
  Video,
  Mic,
  Users,
  FileText,
  Target,
  CheckCircle2,
  GraduationCap,
  Stethoscope,
  ClipboardCheck,
  Theater,
  Tag,
} from 'lucide-react';

interface SimulationCardProps {
  simulation: Simulation;
}

/** Strip HTML tags from a string for clean display */
function stripHtml(text: string): string {
  return text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

export function SimulationCard({ simulation }: SimulationCardProps) {
  const navigate = useNavigate();

  const typeConfig: Record<string, { label: string; gradient: string; badgeColor: string; icon: ReactNode }> = {
    coach: {
      label: 'Coach',
      gradient: 'from-amber-700 to-orange-500',
      badgeColor: 'bg-orange-500 text-white',
      icon: <GraduationCap size={14} />,
    },
    patient: {
      label: 'Roleplay',
      gradient: 'from-emerald-700 to-teal-500',
      badgeColor: 'bg-violet-500 text-white',
      icon: <Stethoscope size={14} />,
    },
    customer: {
      label: 'Roleplay',
      gradient: 'from-slate-700 to-blue-500',
      badgeColor: 'bg-blue-500 text-white',
      icon: <Theater size={14} />,
    },
    examiner: {
      label: 'Examiner',
      gradient: 'from-indigo-700 to-violet-500',
      badgeColor: 'bg-indigo-700 text-white',
      icon: <ClipboardCheck size={14} />,
    },
  };

  const tc = typeConfig[simulation.type] || typeConfig.patient;
  const cleanTitle = simulation.title ? stripHtml(simulation.title) : '';
  const providedFocus = simulation.roleplayFocus ? stripHtml(simulation.roleplayFocus) : '';
  const providedBrief = simulation.briefDescription ? stripHtml(simulation.briefDescription) : '';
  const cleanFocus = providedFocus || 'Fill focus';
  const cleanBrief = providedBrief || 'Fill description';
  const outputItems = simulation.outputItems?.filter(Boolean) || [];
  const recommendedScore = 75;
  const companyName = simulation.companyName ? stripHtml(simulation.companyName) : '';
  const unitName = simulation.unitName ? stripHtml(simulation.unitName) : '';
  const scopeLabel = unitName ? `Unit: ${unitName}` : companyName;
  const scopeTitle = unitName && companyName ? `${companyName} · ${unitName}` : scopeLabel;
  const isVideo = simulation.platform === 'anam';
  // Show the findability category, but suppress the uninformative default.
  const rawCategory = (simulation.category || '').trim();
  const category = rawCategory && rawCategory.toLowerCase() !== 'simulation' ? rawCategory : '';

  return (
    <div
      className="group flex h-full min-h-[440px] cursor-pointer flex-col overflow-hidden rounded-xl border border-border bg-card transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg"
      onClick={() => navigate(`/simulation/${simulation.id}`)}
    >
      {/* Hero Image Area */}
      <div className={`relative h-44 bg-gradient-to-br ${tc.gradient} overflow-hidden`}>
        {(simulation.imageUrl || simulation.avatarUrl) ? (
          <img
            src={simulation.imageUrl || simulation.avatarUrl}
            alt={simulation.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-6xl font-bold text-white/80">{simulation.initials}</span>
          </div>
        )}

        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />

        {/* Top badges */}
        <div className="absolute left-3 top-3 flex max-w-[58%] flex-col items-start gap-1.5">
          <span className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold shadow-sm ${tc.badgeColor}`}>
            {tc.icon}
            <span className="truncate">{tc.label}</span>
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50/90 px-2.5 py-1 text-xs font-semibold text-emerald-950 shadow-sm backdrop-blur">
            {isVideo ? <Video size={15} /> : <Mic size={15} />}
            {isVideo ? 'Video' : 'Audio'}
          </span>
          {category && (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-900/70 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur">
              <Tag size={12} />
              <span className="truncate">{category}</span>
            </span>
          )}
        </div>

        {scopeLabel && (
          <div className="absolute bottom-3 right-3 max-w-[52%]">
            <span
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground shadow-lg"
              title={scopeTitle}
            >
              <Users size={15} />
              <span className="truncate">{scopeLabel}</span>
            </span>
          </div>
        )}

        {/* Name & Title overlay on image */}
        <div className="absolute bottom-0 left-0 right-0 p-3 pr-[48%]">
          <h3 className="line-clamp-1 text-lg font-bold leading-tight text-white drop-shadow-lg" title={simulation.name}>
            {simulation.name}
          </h3>
          {cleanTitle && (
            <p className="mt-0.5 line-clamp-1 text-xs font-medium text-white/90 drop-shadow-md" title={cleanTitle}>{cleanTitle}</p>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <div className="min-h-[96px]">
          <div className="mb-2.5">
            <h4 className={`line-clamp-2 text-lg font-semibold leading-snug ${providedFocus ? 'text-card-foreground' : 'text-muted-foreground italic'}`} title={providedFocus ? cleanFocus : undefined}>
              {cleanFocus}
            </h4>
            <p className={`mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground ${providedBrief ? '' : 'italic'}`} title={providedBrief ? cleanBrief : undefined}>
              {cleanBrief}
            </p>
          </div>

          {outputItems.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-card-foreground">
                <FileText size={17} />
                Output
              </div>
              <div className="mt-1 space-y-0.5 text-sm leading-snug text-muted-foreground">
                {outputItems.slice(0, 2).map((item) => (
                  <p key={item} className="line-clamp-1">{item}</p>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="my-3 grid grid-cols-2 gap-2.5">
          <div className="rounded-lg bg-muted/60 px-2.5 py-3 text-center">
            <div className="flex items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <CheckCircle2 size={12} />
              Completions
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums text-card-foreground">
              {simulation.sessionsCompleted || 0}
            </div>
          </div>
          <div className="rounded-lg bg-muted/60 px-2.5 py-3 text-center">
            <div className="flex items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <Target size={12} />
              Recommended Score
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums text-card-foreground">
              {recommendedScore}%
            </div>
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between gap-3 pt-2">
          <span className="inline-flex min-h-11 min-w-[92px] items-center justify-center rounded-lg bg-emerald-100 px-3 py-2 text-sm font-semibold text-emerald-700">
            Active
          </span>

          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/simulation/${simulation.id}`); }}
            className="inline-flex min-h-11 min-w-[112px] items-center justify-center gap-2 rounded-lg bg-emerald-900 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2"
          >
            <Play size={18} />
            <span>Start Sim</span>
          </button>
        </div>

        {simulation.status !== 'not-started' && (
          <div className="mt-3 text-xs text-muted-foreground">
            Your progress: {simulation.userBestScore || 0}/{simulation.goalScore}
            {simulation.status === 'completed' && (
              <span className="ml-2 font-medium text-emerald-700">Submitted</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
